import { useEffect, useRef, useState, useCallback } from "react";
import { wsUrl } from "@/lib/api";

export function useGameSocket({ code, playerId, name, avatar, enabled }) {
  const [state, setState] = useState(null);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [chat, setChat] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [fatalError, setFatalError] = useState(null);
  const wsRef = useRef(null);
  const attemptsRef = useRef(0);
  const closedRef = useRef(false);
  const handlersRef = useRef({});

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  const on = useCallback((type, fn) => {
    handlersRef.current[type] = fn;
  }, []);

  useEffect(() => {
    if (!enabled || !code || !playerId) return;
    closedRef.current = false;

    const connect = () => {
      const ws = new WebSocket(wsUrl(code, { playerId, name, avatar }));
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        setReconnecting(false);
        attemptsRef.current = 0;
      };
      ws.onmessage = (ev) => {
        let data;
        try { data = JSON.parse(ev.data); } catch (e) { return; }
        const h = handlersRef.current[data.type];
        if (h) h(data);
        if (data.type === "state") {
          setState(data);
          if (Array.isArray(data.chat)) {
            setChat((prev) => {
              const ids = new Set(prev.map((m) => m.id));
              const add = data.chat.filter((m) => !ids.has(m.id));
              return [...prev, ...add].slice(-60);
            });
          }
        } else if (data.type === "chat") {
          setChat((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message].slice(-60)));
        } else if (data.type === "reaction") {
          const rid = Math.random().toString(36).slice(2);
          setReactions((prev) => [...prev, { ...data, rid }]);
          setTimeout(() => setReactions((prev) => prev.filter((r) => r.rid !== rid)), 2600);
        } else if (data.type === "error") {
          setFatalError(data.message);
          closedRef.current = true;
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (closedRef.current) return;
        attemptsRef.current += 1;
        if (attemptsRef.current > 12) { setReconnecting(false); return; }
        setReconnecting(true);
        setTimeout(connect, Math.min(2500, 400 * attemptsRef.current));
      };
      ws.onerror = () => { try { ws.close(); } catch (e) {} };
    };

    connect();
    const ping = setInterval(() => send({ type: "ping" }), 25000);

    return () => {
      closedRef.current = true;
      clearInterval(ping);
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, playerId, enabled]);

  return { state, connected, reconnecting, chat, reactions, send, on, fatalError };
}
